import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment, WsmOptionSetPromptType } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Checkbox, Input, Select, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { getWsmComposeErrorMessage, getWsmComposeFieldError, getWsmComposeFormErrors } from "../../errors";
import { promptTypeLabels } from "../../labels";
import { optionSetMessages, sectionMessages } from "../../messages";
import { WsmOptionSetFormData, WsmOptionValueDraft, WsmProductRef } from "../../types";
import { wsmOptionSetListPath } from "../../urls";
import { countValueChanges, emptyOptionValueDraft, setTierDelta } from "../../utils";
import { WsmTierGroup } from "../WsmOptionValuesCard/columns";
import WsmOptionValuesCard from "../WsmOptionValuesCard/WsmOptionValuesCard";
import WsmProductsField from "../WsmProductsField/WsmProductsField";
import { WsmRail, WsmRailRow } from "../WsmRail/WsmRail";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

export interface WsmOptionSetDetailsPageProps {
  title: string;
  initialData: WsmOptionSetFormData;
  /**
   * Where a new question should land on the product the merchant just picked.
   * Only the create screen passes one; an existing question keeps its own.
   */
  suggestedSortOrder?: number;
  errors: WsmErrorFragment[];
  /** One error per choice index, as the payload named them. */
  valueRowErrors: Record<number, WsmErrorFragment>;
  tierGroups: WsmTierGroup[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmOptionSetFormData) => void;
  /** The create screen watches the products to place a new question after them. */
  onProductsChange?: (products: WsmProductRef[]) => void;
  onDelete?: () => void;
}

export const WsmOptionSetDetailsPage = ({
  title,
  initialData,
  suggestedSortOrder,
  errors,
  valueRowErrors,
  tierGroups,
  disabled,
  saveButtonBarState,
  onSubmit,
  onProductsChange,
  onDelete,
}: WsmOptionSetDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmOptionSetFormData>(initialData);
  // Once the merchant has typed a position it is theirs, and picking a
  // different product must not overwrite it.
  const [sortOrderTouched, setSortOrderTouched] = useState(false);
  const change = (patch: Partial<WsmOptionSetFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const changeValue = (key: string, map: (value: WsmOptionValueDraft) => WsmOptionValueDraft) =>
    setData(current => ({
      ...current,
      values: current.values.map(value => (value.key === key ? map(value) : value)),
    }));

  useEffect(() => {
    if (suggestedSortOrder === undefined || sortOrderTouched) {
      return;
    }

    setData(current =>
      current.sortOrder === suggestedSortOrder
        ? current
        : { ...current, sortOrder: suggestedSortOrder },
    );
  }, [suggestedSortOrder, sortOrderTouched]);

  const changeProducts = (products: WsmProductRef[]) => {
    change({ products });
    onProductsChange?.(products);
  };
  const nameError = getWsmComposeFieldError(errors, "name");
  const labelError = getWsmComposeFieldError(errors, "label");
  const productsError = getWsmComposeFieldError(errors, "products");
  const formErrors = getWsmComposeFormErrors(errors, intl);

  return (
    <DetailPageLayout>
      <TopNav href={wsmOptionSetListPath} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-option-set-general">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(optionSetMessages.generalTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text key={message} size={3} color="critical1" data-test-id="wsm-option-set-error">
                {message}
              </Text>
            ))}
            <WsmProductsField
              value={data.products}
              disabled={disabled}
              error={getWsmComposeErrorMessage(productsError, intl)}
              onChange={changeProducts}
            />
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Input
                label={intl.formatMessage(optionSetMessages.fieldName)}
                data-test-id="wsm-option-set-name"
                disabled={disabled}
                value={data.name}
                onChange={event => change({ name: event.target.value })}
                error={!!nameError}
                helperText={
                  getWsmComposeErrorMessage(nameError, intl) ??
                  intl.formatMessage(optionSetMessages.fieldNameHelp)
                }
              />
              <Input
                label={intl.formatMessage(optionSetMessages.fieldLabel)}
                data-test-id="wsm-option-set-label"
                disabled={disabled}
                value={data.label}
                onChange={event => change({ label: event.target.value })}
                error={!!labelError}
                helperText={
                  getWsmComposeErrorMessage(labelError, intl) ??
                  intl.formatMessage(optionSetMessages.fieldLabelHelp)
                }
              />
            </Box>
            <FormSpacer />
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Select
                label={intl.formatMessage(optionSetMessages.fieldPromptType)}
                data-test-id="wsm-option-set-prompt-type"
                disabled={disabled}
                options={Object.entries(promptTypeLabels).map(([value, message]) => ({
                  value,
                  label: intl.formatMessage(message),
                }))}
                value={data.promptType}
                onChange={value => change({ promptType: value as WsmOptionSetPromptType })}
              />
              <Input
                label={intl.formatMessage(optionSetMessages.fieldSortOrder)}
                data-test-id="wsm-option-set-sort-order"
                type="number"
                disabled={disabled}
                value={String(data.sortOrder)}
                onChange={event => {
                  setSortOrderTouched(true);
                  change({ sortOrder: Number(event.target.value) || 0 });
                }}
                helperText={
                  suggestedSortOrder === undefined
                    ? intl.formatMessage(optionSetMessages.fieldSortOrderHelp)
                    : intl.formatMessage(optionSetMessages.fieldSortOrderHelpDefault, {
                        position: suggestedSortOrder,
                      })
                }
              />
            </Box>
            <FormSpacer />
            <Input
              label={intl.formatMessage(optionSetMessages.fieldNote)}
              data-test-id="wsm-option-set-note"
              disabled={disabled}
              value={data.note}
              onChange={event => change({ note: event.target.value })}
              helperText={intl.formatMessage(optionSetMessages.fieldNoteHelp)}
            />
            <FormSpacer />
            <Checkbox
              data-test-id="wsm-option-set-required"
              disabled={disabled}
              name="required"
              checked={data.required}
              onCheckedChange={checked => change({ required: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(optionSetMessages.fieldRequired)}</Text>
            </Checkbox>
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <WsmOptionValuesCard
          values={data.values}
          rowErrors={valueRowErrors}
          tierGroups={tierGroups}
          disabled={disabled}
          unsavedCount={countValueChanges(initialData.values, data.values)}
          onAdd={() => change({ values: [...data.values, emptyOptionValueDraft(data.values.length)] })}
          onRemove={keys => change({ values: data.values.filter(value => !keys.includes(value.key)) })}
          onValueChange={(key, patch) => changeValue(key, value => ({ ...value, ...patch }))}
          onTierDeltaChange={(key, dealerGroup, raw) =>
            changeValue(key, value => setTierDelta(value, dealerGroup, raw))
          }
        />
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <WsmRail
          title={intl.formatMessage(sectionMessages.railWhereItShows)}
          testId="wsm-option-set-rail"
        >
          <WsmRailRow label={intl.formatMessage(sectionMessages.products)}>
            {data.products.map(product => product.name).join(", ") ||
              intl.formatMessage(sectionMessages.railProductUnset)}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(optionSetMessages.fieldPromptType)}>
            {intl.formatMessage(promptTypeLabels[data.promptType])}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(optionSetMessages.fieldSortOrder)}>
            {String(data.sortOrder)}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(optionSetMessages.colRequired)}>
            <WsmYesNo value={data.required} />
          </WsmRailRow>
        </WsmRail>
      </DetailPageLayout.RightSidebar>

      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmOptionSetListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-option-set-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmOptionSetDetailsPage;
